import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    FileText, ArrowLeft, Loader2, CheckCircle2, AlertTriangle, Pen,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Coach Contract — read + e-sign the currently active version.
 *
 * Shows the current active contract. If the coach hasn't signed THIS
 * version (either never signed or signed an older one), surfaces a
 * signature pad inline. Otherwise shows a green "signed" badge.
 *
 * The same route also acts as a destination for the blocking modal on
 * login — coach can click "Open contract" and sign here.
 */
export default function CoachContract() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [signedName, setSignedName] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user || !["owner", "coach"].includes(user.role)) {
            navigate("/dashboard", { replace: true });
        }
    }, [loading, user, navigate]);

    const load = async () => {
        setBusy(true);
        try {
            const r = await axios.get(`${API}/coach/contract/active`, { withCredentials: true });
            setData(r.data);
            // Pre-fill the signature with the coach's name to make it one-click.
            if (!r.data.is_current) {
                setSignedName(user?.name || "");
            }
        } catch (e) {
            toast.error("Couldn't load contract", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { if (!loading && user) load(); }, [loading, user]);

    const sign = async (e) => {
        e.preventDefault();
        if (!signedName.trim()) return;
        setSubmitting(true);
        try {
            await axios.post(
                `${API}/coach/contract/sign`,
                { signed_name: signedName.trim() },
                { withCredentials: true },
            );
            toast.success("Contract signed", { description: "Thanks — you're all set." });
            await load();
        } catch (e) {
            toast.error("Couldn't sign", { description: e?.response?.data?.detail });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || busy || !data) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-brand-red" />
            </div>
        );
    }

    const { contract, signature, is_current } = data;
    const isOwner = user?.role === "owner";
    const needsResign = signature && !is_current;

    return (
        <div className="min-h-screen bg-black text-white">
            <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
                <div className="flex items-center gap-3 mb-4">
                    <Link
                        to="/coach/dashboard"
                        data-testid="contract-back"
                        className="text-zinc-400 hover:text-white inline-flex items-center gap-2 text-sm"
                    >
                        <ArrowLeft className="w-4 h-4" /> Dashboard
                    </Link>
                </div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-3">
                    <span className="inline-block w-10 h-px bg-brand-red" />
                    Coach console · Contract
                </div>
                <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                    <div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-wider leading-[0.92] text-white">
                            {contract.title.toUpperCase()}
                        </h1>
                        <p className="mt-3 text-zinc-400 text-sm">
                            Version {contract.version} · Updated {new Date(contract.updated_at).toLocaleDateString()}
                        </p>
                    </div>
                    {isOwner && (
                        <Link
                            to="/coach/coaches/contract-editor"
                            data-testid="contract-editor-link"
                            className="btn-ghost-tactical inline-flex items-center gap-2 text-xs"
                        >
                            <Pen className="w-4 h-4" /> Edit contract
                        </Link>
                    )}
                </div>

                {/* Status banner */}
                {is_current ? (
                    <div data-testid="contract-status-signed" className="mb-8 border border-emerald-700/50 bg-emerald-950/30 rounded-2xl px-6 py-4 flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0" />
                        <div className="text-sm">
                            <div className="text-emerald-200 font-medium">You&apos;ve signed this version.</div>
                            <div className="text-emerald-300/70 text-xs mt-0.5">
                                Signed as <span className="text-white">{signature?.signed_name}</span> on {new Date(signature?.signed_at).toLocaleString()}.
                            </div>
                        </div>
                    </div>
                ) : (
                    <div data-testid="contract-status-unsigned" className="mb-8 border border-amber-700/50 bg-amber-950/30 rounded-2xl px-6 py-4 flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <div className="text-amber-200 font-medium">
                                {needsResign
                                    ? `Contract updated — please re-sign (you previously signed v${signature.version}).`
                                    : "You haven't signed this contract yet."}
                            </div>
                            <div className="text-amber-300/70 text-xs mt-0.5">
                                Read the terms below and sign at the bottom to continue.
                            </div>
                        </div>
                    </div>
                )}

                {/* Contract body */}
                <article className="border border-zinc-900 bg-zinc-950/40 rounded-2xl px-8 py-8 mb-8">
                    <pre className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-zinc-200">
                        {contract.body_markdown}
                    </pre>
                </article>

                {/* Signature pad — only when not current */}
                {!is_current && (
                    <form onSubmit={sign} data-testid="contract-sign-form" className="border border-brand-red/40 bg-zinc-950 rounded-2xl px-8 py-6">
                        <h3 className="text-xs tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-2">
                            <Pen className="w-4 h-4" /> Sign below
                        </h3>
                        <p className="text-zinc-400 text-sm mb-4">
                            Type your full legal name to electronically sign this contract. You can revisit and re-sign later if the contract is updated.
                        </p>
                        <input
                            type="text"
                            data-testid="contract-sign-name"
                            value={signedName}
                            onChange={(e) => setSignedName(e.target.value)}
                            placeholder="Full legal name"
                            required
                            className="w-full bg-zinc-900 border border-zinc-800 text-white text-lg px-4 py-3 rounded focus:outline-none focus:border-brand-red mb-4"
                            style={{ fontFamily: "Caveat, cursive" }}
                        />
                        <div className="flex items-center justify-between">
                            <p className="text-xs text-zinc-500">
                                By clicking <span className="text-white">Sign contract</span>, you agree to be legally bound by the terms above.
                            </p>
                            <button
                                type="submit"
                                data-testid="contract-sign-submit"
                                disabled={!signedName.trim() || submitting}
                                className="btn-tactical inline-flex items-center gap-2 disabled:opacity-50"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                {submitting ? "Signing…" : "Sign contract"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
